import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import FilterListIcon from "@material-ui/icons/FilterList";
import { convertToLocalDate } from "../../utils/date";
import {
  AppBar,
  Dialog,
  DialogContent,
  DialogContentText
} from "@material-ui/core";
import { apiRequest } from "../../utils/api_service";
import { API_URL } from "../../constants/api";
import { PaperComponent } from "../../utils/helper";
import { IoMdCloseCircleOutline } from "react-icons/io";
import _ from "lodash"

/**
 * descending comparator function 
 * @param {*} a 
 * @param {*} b 
 * @param {*} orderBy 
 * @param {*} convertToNumber 
 * @returns 
 */
function descendingComparator(a, b, orderBy, convertToNumber) {
  const aValue = convertToNumber ? parseInt(a[orderBy] || 0) : a[orderBy] || "";
  const bValue = convertToNumber ? parseInt(b[orderBy] || 0) : b[orderBy] || "";

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

/**
 * call for desending comparator function
 * @param {*} order 
 * @param {*} orderBy 
 * @param {*} convertToNumber 
 * @returns 
 */
function getComparator(order, orderBy, convertToNumber) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy, convertToNumber)
    : (a, b) => -descendingComparator(a, b, orderBy, convertToNumber);
}

/**
 * sorting
 * @param {*} array 
 * @param {*} comparator 
 * @returns 
 */
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

/**
 * enhanced tabled head function
 * @param {*} props 
 * @returns 
 */
function EnhancedTableHead(props) {
  const {
    classes,
    order,
    orderBy,
    onRequestSort,
    headCells,
    headcellClass
  } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {(headCells || []).map(headCell => {
          //console.log(headCell);
          return (
            <TableCell
              key={headCell.key}
              sortDirection={orderBy === headCell.key ? order : false}
              className={headcellClass || ""}
              style={{ minWidth: headCell.width + "px" }}
            >
              <TableSortLabel
                active={orderBy === headCell.key}
                style={{ textTransform: "capitalize" }}
                direction={orderBy === headCell.key ? order : "asc"}
                onClick={createSortHandler(headCell.key)}
              >
                {headCell.name}
                {orderBy === headCell.key ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

/**
 * table head function properties
 */
EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired
};

/**
 * styling function
 */
const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85)
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        },
  title: {
    flex: "1 1 100%"
  }
}));

const EnhancedTableToolbar = props => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0
      })}
    >
      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color={"inherit"}
          variant={"subtitle1"}
          component={"div"}
        >
          {numSelected} {"selected\r"}
        </Typography>
      ) : (
        <Typography
          className={classes.title}
          variant={"h6"}
          id={"tableTitle"}
          component={"div"}
        >
          {"Nutrition\r"}
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title={"Delete"}>
          <IconButton aria-label={"delete"}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={"Filter list"}>
          <IconButton aria-label={"filter list"}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired
};

/**
 * CSS styling function.
 */
const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    marginTop: "5px"
  },
  paper: {
    width: "100%",
    marginBottom: "5px",
    marginTop: "5px"
  },
  table: {
    minWidth: 750
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  }
}));

/**
 * dense table setup with child function
 * @param {*} param0 
 * @returns 
 */
export default function DenseTable({
  columns,
  data,
  config = {},
  ignoreColumns = []
}) {
  const classes = useStyles();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("nam");
  const [page, setPage] = React.useState(0);
 // const [dense, setDense] = React.useState(true);
  const dense=true;

  const [rowsPerPage, setRowsPerPage] = React.useState(
    config.PageSize || (config != {} && config.minRows != undefined)
      ? config.minRows
      : 5
  );
  const rowsPerPageOptions = [
    5,
    10,
    25,
    50
  ];
  const showPager=(
    config != {} && config.minRows != undefined && data.length < config.minRows
      ? false
      : true
  );
  const [showNotes, setShowNotes] = React.useState({ visible: false });
  const handleRequestSort = (event, property) => {
    //console.log(event);
    //console.log(orderBy);

    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /**
   * on change page call
   * @param {*} event 
   * @param {*} newPage 
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * call on change rows per page option change
   * @param {*} event 
   */
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  if (data.length == 1) {
    let hasValue = false;
    Object.entries(data[0]).forEach(obj => {
      if (obj[1] !== null) hasValue = true;
    });
    if (hasValue !== true) {
      data = [];
    }
  }
  const notesColumns = [
    { key: "NoteSerialNumber", name: "Note Number" },
    { key: "Note", name: "Note" },
    { key: "NoteCreatedBy", name: "Note Created By" },
    { key: "NoteCreatedDate", name: "Note Created On" }
  ];

  /**
   * call on notes click
   * @param {*} type 
   * @param {*} cellInfo 
   */
  const handelNotesClick = (type, cellInfo) => {
    apiRequest({
      url:
        API_URL.getNotes +
        "?workOrderNumber=" +
        cellInfo.WorkOrderId +
        "&isPrivateNote=" +
        (type == "PrivateNote") +
        "&sourceInfo=" +
        cellInfo.Source,
      params: { isHideLoader: false }
    }).then(({ data }) => {
      setShowNotes({
        visible: true,
        notes: data || [],
        isPrivate: type == "PrivateNote"
      });
    });
  };

  /**
   * get formatted data
   * @param {*} value 
   * @param {*} col 
   * @param {*} row 
   * @param {*} config 
   * @returns 
   */
  const getFormattedData = (value, col, row, config) => {
    if (col.clickable) {
      let className = "";
      if (value && value.replace("-", "").length > 0) {
        className += "pointer underline";
      }

      return (
        <div>
          <Tooltip
            title={
              value && value.replace("-", "").length > 0
                ? "View all " + col.name
                : ""
            }
          >
            <span
              onClick={() => {
                handelNotesClick(col.key, row);
              }}
              className={className}
            >
              {value}
            </span>
          </Tooltip>
        </div>
      );
    }
    if (col.format) {
      if (col.format.toLowerCase() === "DateTime".toLowerCase()) {
        return convertToLocalDate(value, "MM/DD/YYYY hh:mm A");
      }
    }

    if (config.htmlFormat && config.htmlFormat === true) {
      return <div dangerouslySetInnerHTML={{ __html: value }} />;
    }
    return value;
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  columns = columns.filter(
    ar =>
      !(ignoreColumns || []).find(rm => _.camelCase(rm) === _.camelCase(ar.key))
  );

  let assetId = "";
  let workOrderId = "";
  if (showNotes.notes && showNotes.notes.length > 0) {
    assetId = showNotes.notes[0].AssetId || "";
    workOrderId = showNotes.notes[0].WorkOrderId || "";
  }

  return (
    <>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby={"tableTitle"}
              size={dense ? "small" : "medium"}
              aria-label={"enhanced table"}
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                rowCount={data.length}
                onRequestSort={handleRequestSort}
                headCells={columns}
                headcellClass={config.headcellClass}
              />
              <TableBody>
                {showPager &&
                  stableSort(
                    data,
                    getComparator(
                      order,
                      orderBy,
                      config.convertToNumber || false
                    )
                  )
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <TableRow
                          className={
                            index % 2 == 0
                              ? config.evenRowClass
                              : config.oddRowClass
                          }
                          key={"table_data" + index}
                        >
                          {(columns || []).map((col, i) => (
                            <TableCell
                              key={"tableRowCell" + index + i + col.key}
                              style={{ minWidth: col.width + "px" }}
                            >
                              {col.renderComponent !== undefined
                                ? col.renderComponent(row, col.key)
                                : getFormattedData(
                                    row[col.key],
                                    col,
                                    row,
                                    config
                                  )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                {showPager == false &&
                  stableSort(
                    data,
                    getComparator(
                      order,
                      orderBy,
                      config.convertToNumber || false
                    )
                  ).map((row, index) => {
                    return (
                      <TableRow
                        className={
                          index % 2 == 0
                            ? config.evenRowClass
                            : config.oddRowClass
                        }
                        key={"pager-false-table_data" + index}
                      >
                        {(columns || []).map((col, i) => (
                          <TableCell
                            key={"pager-false-tableRowCell" + index + i + col.key}
                            style={{ minWidth: col.width + "px" }}
                          >
                            {col.renderComponent !== undefined
                              ? col.renderComponent(row, col.key)
                              : getFormattedData(row[col.key], col, row, config)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                {data == [] ||
                  (data.length == 0 && (
                    <TableRow
                      key={"emptyRow"}
                      style={{ height: dense ? 33 : 53 }}
                    >
                      <TableCell key={"emptyRowCell"} colSpan={6}>
                        {"No data available\r"}
                      </TableCell>
                    </TableRow>
                  ))}
                {showPager && emptyRows > 0 && (
                  <TableRow
                    key={"emptyRow1"}
                    style={{ height: (dense ? 33 : 53) * emptyRows }}
                  >
                    <TableCell key={"emptyRowCell1"} colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!config?.pagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component={"div"}
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </div>
      {showNotes.visible == true && (
        <Dialog
          open={showNotes.visible}
          maxWidth={"md"}
          onClose={() => setShowNotes({ visible: false, notes: [] })}
          aria-labelledby={"responsive-dialog-title"}
          PaperComponent={PaperComponent}
        >
          <AppBar style={{ position: "relative", backgroundColor: "#064473" }}>
            <Toolbar>
              <Typography
                variant={"h6"}
                style={{ marginLeft: "10px", flex: "1" }}
              >
                {showNotes.isPrivate ? "Private Notes" : "Public Notes"}
              </Typography>
              <Tooltip title={"Close"}>
                <IconButton
                  edge={"start"}
                  color={"inherit"}
                  onClick={() => {
                    setShowNotes({
                      visible: false,
                      notes: []
                    });
                  }}
                  aria-label={"close"}
                >
                  <IoMdCloseCircleOutline />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>

          <DialogContent>
            <DialogContentText>
              <div className={"notes-group"}>
                <b>{"Asset Id: "}</b>{" "}
                <span style={{ fontWeight: 400, fontSize: "small" }}>
                  {assetId}
                </span>
              </div>
              <div className={"notes-group"}>
                <b>{"WorkOrder Id: "}</b>{" "}
                <span style={{ fontWeight: 400, fontSize: "small" }}>
                  {workOrderId}
                </span>
              </div>
              <div>
                <DenseTable
                  columns={notesColumns}
                  data={showNotes.notes}
                  ignoreColumns={ignoreColumns}
                  config={{
                    minRows: 10,
                    htmlFormat: true
                    // evenRowClass: "table-even",
                    // oddRowClass: "table-odd",
                  }}
                />
              </div>

              {/* {syncStatus && (
						<span className="data-sync-text">{getSynchStatus(syncStatus)}</span>
					)} */}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
